import { useEffect, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import {
  buildFuseIndices,
  searchPerFuseIndex,
} from '../local/buildFuseIndices'
import { searchDebugLog } from '../searchDebugLog'

/**
 * Headless shadow search provider for Fuse-scored Redux-derived library
 * content.
 *
 * Implements the universal provider interface defined in
 * `src/readmes/SEARCH_ARCHITECTURE.md`. The orchestrator renders this as a
 * sibling of the display provider; results are routed to shadow state and
 * fed to searchReporter (not shown to the user) under providerId='fuse-content'.
 *
 * Output shape mirrors AlgoliaSearchProvider exactly so the same UI
 * primitives (IndexHitList / SectionHeader / per-index totals) can render
 * either side without duplication:
 *   {
 *     suggestions: [{ index, hits }, ...],
 *     hits:        <flat list across all indices>,
 *     indexResults: {
 *       repos:          { hits, hitCount, hasMore, loadMore },
 *       folders:        { hits, hitCount, hasMore, loadMore },
 *       sets_questions: { hits, hitCount, hasMore, loadMore },
 *     }
 *   }
 *
 * Each emitted hit copies the corresponding Redux record and adds:
 *   - objectID:      Algolia-compatible primary key (= record id)
 *   - __localOrigin: true marker so the UI can render a client-origin badge
 *   - __fuseScore:   Fuse score (lower = stronger match)
 *   - __fuseMatches: per-key match metadata (Fuse `matches`)
 */
function FuseContentSearchProvider({
  query, options, onResults,
  sets, questions, folders, repos,
}) {
  const indices = useMemo(
    () => buildFuseIndices({
      sets, questions, folders, repos,
    }),
    [sets, questions, folders, repos],
  )

  const onResultsRef = useRef(onResults)
  onResultsRef.current = onResults

  useEffect(() => {
    if (!query) {
      onResultsRef.current({
        suggestions: [],
        hits: [],
        indexResults: undefined,
      })
      return
    }
    const limit = options && options.hitsPerPage ? options.hitsPerPage : 10
    const indexResults = searchPerFuseIndex(indices, query, limit)
    const flatHits = [
      ...indexResults.repos.hits,
      ...indexResults.folders.hits,
      ...indexResults.sets_questions.hits,
    ]
    const suggestions = [
      { index: 'repos', hits: indexResults.repos.hits },
      { index: 'folders', hits: indexResults.folders.hits },
      { index: 'sets_questions', hits: indexResults.sets_questions.hits },
    ]
    onResultsRef.current({
      suggestions,
      hits: flatHits,
      indexResults,
    })
    searchDebugLog('[local-search:fuse-content]', {
      query,
      limit,
      hitCount: flatHits.length,
      indexResults,
      hits: flatHits,
    })
  }, [query, indices, options])

  return null
}

function mapStateToProps(state) {
  return {
    sets: state.sets,
    questions: state.questions,
    folders: state.folders,
    repos: state.repos,
  }
}

export default connect(mapStateToProps)(FuseContentSearchProvider)
